import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Grid } from '@mui/material'
import { useSelector } from 'react-redux'
import { fromJS } from 'immutable'
import { getContentList, updateContent , getFilteredDisplayOptions } from 'services/content'
import { selectAuth } from 'services/auth/redux/selectors'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import MuiCheckbox from 'gui/components/common/MuiCheckbox'
import { getListCriteriaInitialValue } from './helpers'

export interface ListCriteriaItem {
  contentType: string
  productType?: string
}

export interface FilteredDisplayOptionsSectionProps {
  contentId?: string | number
  isViewOnly?: boolean
  onDirtyChange: (isDirty: boolean) => void
  saveHandlerRef: React.MutableRefObject<(() => Promise<void>) | null>
  onResetTriggered?: number
}

/**
 * Section-managed component for tag "Filtered Display Options" (listCriteria).
 * Fetches and saves listCriteria via REST API; parent triggers save via saveHandlerRef.
 * Visibility is gated by editor-plus/admin in the parent (TagDetailV2).
 */
function FilteredDisplayOptionsSection (props: FilteredDisplayOptionsSectionProps) {
  const {
    contentId,
    isViewOnly = false,
    onDirtyChange,
    saveHandlerRef,
    onResetTriggered = 0,
  } = props

  const auth = useSelector(selectAuth)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const [listCriteriaAll, setListCriteriaAll] = useState<ListCriteriaItem[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const listCriteriaAllRef = useRef<ListCriteriaItem[]>(listCriteriaAll)
  listCriteriaAllRef.current = listCriteriaAll

  const fetchListCriteria = useCallback(async () => {
    if (contentId == null || !brooklynUrl) return
    setLoading(true)
    setFetchError(null)
    try {
      // getContentList accepts id + include (REST detail fetch); TS types are narrowed to content-array overload
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await (getContentList as (opts: Record<string, unknown>) => Promise<any>)({
        auth,
        server: brooklynUrl,
        id: contentId,
        include: ['content_list_criteria'],
      })
      const content = response?.getIn?.(['content', 0])
      if (content) {
        const initial = getListCriteriaInitialValue(content)
        const all = (initial?.criteria?.all ?? []) as ListCriteriaItem[]
        setListCriteriaAll(all)
      } else {
        setListCriteriaAll([])
      }
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load filtered display options')
      setListCriteriaAll([])
    } finally {
      setLoading(false)
    }
  }, [auth, brooklynUrl, contentId])

  useEffect(() => {
    fetchListCriteria()
  }, [fetchListCriteria, contentId, onResetTriggered])

  useEffect(() => {
    saveHandlerRef.current = async () => {
      const id = contentId
      if (id == null || !brooklynUrl) return
      const data = fromJS({
        id,
        listCriteria: {
          criteria: {
            all: listCriteriaAllRef.current,
          },
        },
      })
      await updateContent({
        auth,
        server: brooklynUrl,
        data,
      })
      onDirtyChange(false)
    }
    return () => {
      saveHandlerRef.current = null
    }
  }, [auth, brooklynUrl, contentId, onDirtyChange, saveHandlerRef])

  const optionsArray = getFilteredDisplayOptions().toArray()

  const isChecked = useCallback(
    (value: [string, string?]) => {
      const [contentType, productType] = value
      return listCriteriaAll.some(
        (item) =>
          item.contentType === contentType &&
          (item.productType ?? undefined) === (productType ?? undefined),
      )
    },
    [listCriteriaAll],
  )

  const handleChange = useCallback(
    (value: [string, string?], checked: boolean) => {
      const [contentType, productType] = value
      setListCriteriaAll((prev) => {
        if (checked) {
          const newItem: ListCriteriaItem = { contentType }
          if (productType) newItem.productType = productType
          return [...prev, newItem]
        }
        return prev.filter(
          (item) =>
            !(
              item.contentType === contentType &&
              (item.productType ?? undefined) === (productType ?? undefined)
            ),
        )
      })
      onDirtyChange(true)
    },
    [onDirtyChange],
  )

  if (loading && listCriteriaAll.length === 0 && !fetchError) {
    return null
  }

  if (fetchError) {
    return (
      <SectionAccordion title="Filtered Display Options" collapsed={false}>
        <div style={{ color: 'var(--mui-palette-error-main)' }}>{fetchError}</div>
      </SectionAccordion>
    )
  }

  return (
    <div className="content-detail__section filtered-display">
      <SectionAccordion title="Filtered Display Options" collapsed>
        <Grid container spacing={2} alignItems="center">
          {optionsArray.map((option) => {
            const value = option.get('value') as [string, string?]
            const label = option.get('label') as string
            return (
              <Grid item xs={3} key={label}>
                <MuiCheckbox
                  label={label}
                  name="listCriteria.criteria.all"
                  value={value}
                  onChange={(e) => {
                    const { checked } = e.target
                    handleChange(value, checked)
                  }}
                  checked={isChecked(value)}
                  disabled={isViewOnly}
                />
              </Grid>
            )
          })}
        </Grid>
      </SectionAccordion>
    </div>
  )
}

export default FilteredDisplayOptionsSection
